import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal, computed } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, finalize, forkJoin, map, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import type { EnvironmentFeatures } from '../../environments/environment.types';
import { resolveRestaurantLogoUrl } from '../config/default-assets';
import type {
  ContactInfoDto,
  GalleryImageDto,
  HighlightDto,
  MenuDto,
  OpeningHoursDto,
  RestaurantProfileDto,
  TenantFeaturesDto,
  TenantThemeDto,
} from '../models/restaurant-api.model';

export interface RestaurantUiConfig {
  name: string;
  logo: string;
  theme: string;
  primaryColor: string;
  secondaryColor: string;
  features: EnvironmentFeatures;
}

/**
 * Loads public tenant data from the backend (`/api/profile`, `/api/menu`, `/api/theme`,
 * `/api/features`, `/api/contact`, `/api/opening-hours`) and applies deployment-level branding from `environment`.
 */
@Injectable({ providedIn: 'root' })
export class RestaurantConfigService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);

  readonly profile = signal<RestaurantProfileDto | null>(null);
  readonly menu = signal<MenuDto | null>(null);
  readonly tenantTheme = signal<TenantThemeDto | null>(null);
  readonly tenantFeatures = signal<TenantFeaturesDto | null>(null);
  readonly contactInfo = signal<ContactInfoDto | null>(null);
  readonly openingHours = signal<OpeningHoursDto[] | null>(null);
  readonly gallery = signal<GalleryImageDto[]>([]);
  readonly highlights = signal<HighlightDto[]>([]);
  readonly initialized = signal(false);
  readonly loadFailed = signal(false);

  readonly config = computed<RestaurantUiConfig>(() => {
    const p = this.profile();
    const tf = this.tenantFeatures();
    const envF: EnvironmentFeatures = environment.features;
    return {
      name: p?.name ?? 'Kanyar Büfé',
      logo: resolveRestaurantLogoUrl(p),
      theme: this.tenantTheme()?.layoutTheme ?? 'classic',
      primaryColor: environment.branding.primaryColor,
      secondaryColor: environment.branding.secondaryColor,
      features: {
        reservation:
          tf != null ? tf.reservation : environment.production ? false : envF.reservation,
        adminPanel: tf?.adminPanel ?? envF.adminPanel,
        analytics: envF.analytics,
        googleReviews: tf?.googleReviews ?? envF.googleReviews,
      },
    };
  });

  private readonly priceFormat = new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  });

  formatPrice(value: number): string {
    return this.priceFormat.format(value);
  }

  load(): Observable<void> {
    return forkJoin({
      profile: this.http
        .get<RestaurantProfileDto>(`${environment.apiUrl}/api/profile`)
        .pipe(catchError(() => of(null))),
      menu: this.http
        .get<MenuDto>(`${environment.apiUrl}/api/menu`)
        .pipe(catchError(() => of(null))),
      theme: this.http
        .get<TenantThemeDto>(`${environment.apiUrl}/api/theme`)
        .pipe(catchError(() => of(null))),
      features: this.http
        .get<TenantFeaturesDto>(`${environment.apiUrl}/api/features`)
        .pipe(catchError(() => of(null))),
      contact: this.http
        .get<ContactInfoDto>(`${environment.apiUrl}/api/contact`)
        .pipe(catchError(() => of(null))),
      openingHours: this.http
        .get<OpeningHoursDto[]>(`${environment.apiUrl}/api/opening-hours`)
        .pipe(catchError(() => of(null))),
      gallery: this.http
        .get<GalleryImageDto[]>(`${environment.apiUrl}/api/gallery`)
        .pipe(catchError(() => of(null))),
      highlights: this.http
        .get<HighlightDto[]>(`${environment.apiUrl}/api/highlights`)
        .pipe(catchError(() => of(null))),
    }).pipe(
      tap(({ profile, menu, theme, features, contact, openingHours, gallery, highlights }) => {
        if (profile) {
          this.profile.set(profile);
        } else {
          this.loadFailed.set(true);
        }
        if (menu?.categories?.length) {
          this.menu.set(menu);
        }
        if (theme) {
          this.tenantTheme.set(theme);
        }
        if (features) {
          this.tenantFeatures.set(features);
        }
        if (contact) {
          this.contactInfo.set(contact);
        }
        if (openingHours?.length) {
          this.openingHours.set(openingHours);
        }
        if (gallery?.length) {
          this.gallery.set(gallery);
        }
        if (highlights?.length) {
          this.highlights.set(highlights);
        }
        this.applyBranding();
      }),
      finalize(() => this.initialized.set(true)),
      map(() => undefined)
    );
  }

  private applyBranding(): void {
    const root = this.document.documentElement;
    const { primaryColor, secondaryColor } = environment.branding;
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-secondary', secondaryColor);
    root.style.setProperty('--color-accent', primaryColor);
    root.style.setProperty('--color-success', primaryColor);
    const hover = adjustLightness(primaryColor, -8);
    root.style.setProperty('--color-accent-hover', hover);
    root.dataset['layoutTheme'] = this.tenantTheme()?.layoutTheme ?? 'classic';
  }
}

function adjustLightness(hex: string, delta: number): string {
  const m = hex.replace('#', '').trim();
  if (m.length !== 6) {
    return hex;
  }
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const nr = clamp(r + delta);
  const ng = clamp(g + delta);
  const nb = clamp(b + delta);
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
}

function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function toHex(n: number): string {
  return n.toString(16).padStart(2, '0');
}
